import React, { useMemo, useState } from "react";
import {
    getToken,
    createLoan,
    getLoanStatus,
    getRepaymentSchedule,
    initiateNach,
    createAgreement,
    loanDetails,
    uploadDocuments,
    retrieveDocuments,
    viewAgreement,
    initiateUpiPayment,
    getPayments,
} from "../api/lspClient";

const pretty = (v) => {
    try {
        if (typeof v === "string") return v;
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
};

const defaultLoanBody = {
    loan_id: "7545339",
    amount: 50000,
    max_amount_applied: 50000,
    tenure: 12,
    periodicity: "monthly",
    creation_date: "2024-05-25",
    total_processing_fee: 1,
    broken_period_interest: 500,
    interest_rate: 33,
    aadhar_number: 123434565678,
    pan_number: "KHYBP3212J",
    user_phone: 1100229933,
    name: "Ankit",
    father_name: "test",
    date_of_birth: "2001-03-24",
    gender: "male",
    marital_status: "unmarried",
    education_level: "graduate",
    employer_name: "tectcom",
    date_of_joining: "2024-05-24",
    employment_type: "salaried",
    email: "someone@example.com",
    monthly_salary: 15000,
    family_members: 3,
    monthly_family_income: 40000,
    foir_score: 0.4,
    bank_account: 1233536547658754,
    ifsc_code: "JNUB1116433",
    name_alt_bank_account_holder: "",
    alternate_bank_account: "",
    alternate_ifsc_code: "",
    applicant_type: "co_borrower",
    purpose: "Medical",
    credit_profile: {
        bureau_name: "crif",
        score: 0,
        total_existing_obligation: 0,
        number_of_writtenoff_in_12_months: 0,
    },
    address: {
        permanent_address: "",
        permanent_address_city: "",
        permanent_address_state: "",
        permanent_address_pin: "",
        current_address: "",
        current_address_city: "",
        current_address_state: "",
        current_address_pin: "",
        current_address_owned: "",
        current_address_landmark: "",
        raw_data: "",
    },
};

export default function LspTester() {
    const [baseUrl] = useState(process.env.REACT_APP_LSP_BASE_URL || "");
    const [username, setUsername] = useState(process.env.REACT_APP_LSP_USERNAME || "");
    const [password, setPassword] = useState(process.env.REACT_APP_LSP_PASSWORD || "");

    const [token, setToken] = useState("");
    const [partnerLoanId, setPartnerLoanId] = useState(""); // use loan_id returned as "loan_id" in response (partner loan id)
    const [loanBodyText, setLoanBodyText] = useState(pretty(defaultLoanBody));

    const [scheduleStartDate, setScheduleStartDate] = useState("23-March-2025");
    const [nachMode, setNachMode] = useState("esign"); // "esign" or "upi"
    const [upiAmount, setUpiAmount] = useState(1);
    const [paymentId, setPaymentId] = useState("");

    const [docType, setDocType] = useState("pan");
    const [docFile, setDocFile] = useState(null);

    const [loading, setLoading] = useState(false);
    const [output, setOutput] = useState("");

    const canCallAuthed = useMemo(() => Boolean(token && partnerLoanId), [token, partnerLoanId]);

    const run = async (fn) => {
        setLoading(true);
        setOutput("");
        try {
            const res = await fn();
            setOutput(pretty(res));
        } catch (e) {
            setOutput(`❌ ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto", fontFamily: "system-ui, Arial" }}>
            <h2 style={{ marginBottom: 4 }}>LSP API Tester (React)</h2>
            <div style={{ opacity: 0.7, marginBottom: 16 }}>Base URL: {baseUrl || "(set REACT_APP_LSP_BASE_URL in .env)"}</div>

            {/* Auth */}
            <div style={card}>
                <h3 style={h3}>1) Get JWT Token</h3>
                <div style={row}>
                    <input style={input} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" />
                    <input
                        style={input}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="password"
                        type="password"
                    />
                    <button
                        style={btn}
                        disabled={loading}
                        onClick={() =>
                            run(async () => {
                                const res = await getToken(username, password);
                                if (res?.token) setToken(res.token);
                                return res;
                            })
                        }
                    >
                        Get Token
                    </button>
                </div>

                <div style={{ marginTop: 10 }}>
                    <label style={label}>Token</label>
                    <textarea
                        style={{ ...textarea, height: 80 }}
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="JWT token will appear here"
                    />
                </div>
            </div>

            {/* Create Loan */}
            <div style={card}>
                <h3 style={h3}>2) Submit Loan Data (Create Loan)</h3>

                <label style={label}>Request Body (JSON)</label>
                <textarea
                    style={{ ...textarea, height: 240 }}
                    value={loanBodyText}
                    onChange={(e) => setLoanBodyText(e.target.value)}
                />

                <div style={{ ...row, marginTop: 10 }}>
                    <button
                        style={btn}
                        disabled={loading || !token}
                        onClick={() =>
                            run(async () => {
                                const body = JSON.parse(loanBodyText);
                                const res = await createLoan(token, body);
                                // PDF response shows loan_id returned; treat it as partner_loan_id for next calls
                                if (res?.loan_id) setPartnerLoanId(res.loan_id);
                                return res;
                            })
                        }
                    >
                        Create Loan
                    </button>

                    <input
                        style={input}
                        value={partnerLoanId}
                        onChange={(e) => setPartnerLoanId(e.target.value)}
                        placeholder="partner_loan_id (loan_id returned)"
                    />
                </div>
            </div>

            {/* Status + Schedule */}
            <div style={card}>
                <h3 style={h3}>3) Loan Status + Repayment Schedule</h3>

                <div style={row}>
                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() => run(() => getLoanStatus(token, partnerLoanId))}
                    >
                        Get Loan Status
                    </button>

                    <input
                        style={input}
                        value={scheduleStartDate}
                        onChange={(e) => setScheduleStartDate(e.target.value)}
                        placeholder='start_date e.g. "23-March-2025"'
                    />

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() => run(() => getRepaymentSchedule(token, partnerLoanId, scheduleStartDate))}
                    >
                        Get Repayment Schedule
                    </button>
                </div>
            </div>

            {/* NACH + Agreement */}
            <div style={card}>
                <h3 style={h3}>4) Initiate NACH + Create/View Agreement</h3>

                <div style={row}>
                    <select style={input} value={nachMode} onChange={(e) => setNachMode(e.target.value)}>
                        <option value="esign">eSign (variant: esign/api)</option>
                        <option value="upi">UPI NACH (is_setu: true)</option>
                    </select>

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() =>
                            run(() => {
                                const body = nachMode === "upi" ? { account_type: "savings", is_setu: true } : { account_type: "savings", variant: "esign/api" };
                                return initiateNach(token, partnerLoanId, body);
                            })
                        }
                    >
                        Initiate NACH
                    </button>

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() => run(() => createAgreement(token, partnerLoanId))}
                    >
                        Create Agreement
                    </button>

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() =>
                            run(async () => {
                                const url = await viewAgreement(token, partnerLoanId);
                                // url is string per PDF
                                return { agreement_url: url };
                            })
                        }
                    >
                        View Agreement (URL)
                    </button>
                </div>
            </div>

            {/* Loan details */}
            <div style={card}>
                <h3 style={h3}>5) Loan Details</h3>
                <div style={row}>
                    <button style={btn} disabled={loading || !canCallAuthed} onClick={() => run(() => loanDetails(token, partnerLoanId))}>
                        Get Loan Details
                    </button>
                </div>
            </div>

            {/* Upload docs */}
            <div style={card}>
                <h3 style={h3}>6) Upload / Retrieve Documents</h3>

                <div style={row}>
                    <select style={input} value={docType} onChange={(e) => setDocType(e.target.value)}>
                        <option value="pan">pan</option>
                        <option value="aadhar">aadhar</option>
                        <option value="photo">photo</option>
                        <option value="bank_statement">bank_statement</option>
                        <option value="misc">misc (ex: cibil_report)</option>
                    </select>

                    <input
                        style={{ ...input, padding: 6 }}
                        type="file"
                        onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                    />

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed || !docFile}
                        onClick={() => run(() => uploadDocuments(token, partnerLoanId, docType, docFile))}
                    >
                        Upload Document
                    </button>

                    <button style={btn} disabled={loading || !canCallAuthed} onClick={() => run(() => retrieveDocuments(token, partnerLoanId))}>
                        Retrieve Documents
                    </button>
                </div>
            </div>

            {/* UPI payment + Get payments */}
            <div style={card}>
                <h3 style={h3}>7) UPI Payment + Get Payments</h3>

                <div style={row}>
                    <input
                        style={input}
                        value={upiAmount}
                        onChange={(e) => setUpiAmount(Number(e.target.value || 0))}
                        placeholder="amount"
                        type="number"
                        min="1"
                    />

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() => run(() => initiateUpiPayment(token, partnerLoanId, upiAmount))}
                    >
                        Initiate UPI Payment
                    </button>

                    <input
                        style={input}
                        value={paymentId}
                        onChange={(e) => setPaymentId(e.target.value)}
                        placeholder="payment_id (optional)"
                    />

                    <button
                        style={btn}
                        disabled={loading || !canCallAuthed}
                        onClick={() => run(() => getPayments(token, partnerLoanId, paymentId ? Number(paymentId) : undefined))}
                    >
                        Get Payments
                    </button>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                    {/* Note from PDF: SETU UPI is production; test with small amounts. :contentReference[oaicite:14]{index = 14} */}
                </div>
            </div>

            {/* Output */}
            <div style={card}>
                <h3 style={h3}>Response Output</h3>
                <div style={{ marginBottom: 8 }}>
                    <button style={btn} onClick={() => setOutput("")} disabled={loading}>
                        Clear
                    </button>
                </div>
                <pre style={pre}>{loading ? "⏳ Calling API..." : output || "No output yet."}</pre>
            </div>
        </div>
    );
}

const card = {
    background: "#111827",
    color: "#E5E7EB",
    border: "1px solid #374151",
    borderRadius: 12,
    padding: 14,
    marginBottom: 14,
};

const row = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const input = { padding: 10, borderRadius: 10, border: "1px solid #374151", background: "#fffff", color: "#E5E7EB", minWidth: 240 };
const textarea = { width: "100%", padding: 10, borderRadius: 10, border: "1px solid #374151", background: "#fffff", color: "#E5E7EB", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" };
const btn = { padding: "10px 14px", borderRadius: 10, border: "1px solid #4B5563", background: "#1F2937", color: "#E5E7EB", cursor: "pointer" };
const label = { display: "block", marginBottom: 6, opacity: 0.8 };
const pre = { background: "#fffff", padding: 12, borderRadius: 10, border: "1px solid #374151", overflow: "auto", maxHeight: 360 };
const h3 = { marginTop: 0, marginBottom: 10 };
